import { EXIT_OK, CliError, type CommandOutput } from "./common.js";

function hasRows(data: Record<string, unknown>): data is Record<string, unknown> & {
  rows: Array<Record<string, unknown>>;
} {
  return Array.isArray(data["rows"]);
}

function withNewline(text: string): string {
  return text.endsWith("\n") ? text : `${text}\n`;
}

function diagnosticItems(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item).replace(/[\r\n]+/g, " "));
}

function tsvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.replace(/\t/g, " ").replace(/\r?\n/g, " ");
}

function tableText(data: Record<string, unknown>): string | null {
  if (!hasRows(data) || data.rows.length === 0 || !Array.isArray(data["row_columns"])) return null;
  const columns = data["row_columns"].filter((column): column is string => typeof column === "string");
  if (columns.length === 0) return null;
  return [
    columns.join("\t"),
    ...data.rows.map((row) => columns.map((column) => tsvCell(row[column])).join("\t")),
  ].join("\n");
}

export function emit(output: CommandOutput, exitCode: number, jsonMode: boolean, format?: string): void {
  if (format === "addr" && hasRows(output.data)) {
    if (output.data.rows.length === 0) {
      process.stdout.write("");
      return;
    }
    const addrs = output.data.rows.map((row) => String(row["addr"] ?? ""));
    if (addrs.every((address) => address.length > 0)) {
      process.stdout.write(addrs.join("\n") + "\n");
      return;
    }
  }
  if (jsonMode) {
    const payload: Record<string, unknown> = {
      ok: exitCode === EXIT_OK,
      exit_code: exitCode,
      ...output.data,
    };
    payload["ok"] = exitCode === EXIT_OK;
    payload["exit_code"] = exitCode;
    process.stdout.write(JSON.stringify(payload, null, 2) + "\n");
  } else {
    // A command that already wrote its own stream output (`review` emits
    // NDJSON) returns nothing to print; an envelope newline would be a junk
    // line inside its protocol.
    if (output.text === "" && Object.keys(output.data).length === 0) return;
    process.stdout.write(withNewline(tableText(output.data) ?? output.text));
  }
}

export function emitError(error: CliError, jsonMode: boolean): number {
  if (jsonMode) {
    const payload: Record<string, unknown> = {
      ok: false,
      exit_code: error.exitCode,
      error_code: error.errorCode,
      message: error.message,
      ...(error.hint ? { hint: error.hint } : {}),
      ...error.data,
    };
    payload["ok"] = false;
    payload["exit_code"] = error.exitCode;
    payload["error_code"] = error.errorCode;
    payload["message"] = error.message;
    if (error.hint) payload["hint"] = error.hint;
    process.stdout.write(JSON.stringify(payload, null, 2) + "\n");
  } else {
    const actual = diagnosticItems(error.data["actual"]);
    const expected = diagnosticItems(error.data["expected"]);
    const message = actual.length > 0
      ? `${error.message.replace(/\.$/, "")}: ${actual[0]}`
      : error.message;
    const lines = [`error: ${message}`];
    if (actual.length > 1) lines.push(`actual: ${actual.slice(1).join("; ")}`);
    if (expected.length > 0) lines.push(`expected: ${expected.join("; ")}`);
    if (error.hint) lines.push(error.hint);
    process.stderr.write(`${lines.join("\n")}\n`);
  }
  return error.exitCode;
}
